import React, { useState, useRef, useCallback, useEffect } from "react";
import { useHistoryContext } from "../context/HistoryContext";
import {
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Paper,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Controlled as CodeMirror } from "react-codemirror2";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/material.css";
import "codemirror/mode/javascript/javascript";
import "codemirror/mode/htmlmixed/htmlmixed";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useThemeContext } from "../components/GeneralConfig";

// Animação de digitação segura — nunca concatena undefined
function startTypeEffect(text, setState, delay = 6) {
  let i = 0;
  const id = setInterval(() => {
    i++;
    setState(text.slice(0, i));
    if (i >= text.length) clearInterval(id);
  }, delay);
  return () => clearInterval(id);
}

const Automation = () => {
  const [purpose, setPurpose] = useState("");
  const [code, setCode] = useState("");
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copyMessage, setCopyMessage] = useState("");
  const cancelTypingRef = useRef(null);
  const preRef = useRef(null);
  const { addToHistory } = useHistoryContext();

  // Auto-scroll conforme o script é gerado
  useEffect(() => {
    if (isTyping && preRef.current) {
      preRef.current.scrollTop = preRef.current.scrollHeight;
    }
  }, [result, isTyping]);
  const { darkMode } = useThemeContext();

  const validateCode = (inputCode, inputPurpose) => {
    if (inputPurpose === "Cypress") {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(inputCode, "text/html");
        const elements = doc.querySelectorAll("h1, h2, button, input");
        return elements && elements.length > 0;
      } catch (e) {
        return false;
      }
    } else if (inputPurpose === "Postman") {
      try {
        JSON.parse(inputCode);
        return true;
      } catch (e) {
        return false;
      }
    }
    return false;
  };

  const handleShowFull = useCallback(() => {
    if (cancelTypingRef.current) {
      cancelTypingRef.current();
      cancelTypingRef.current = null;
    }
    setIsTyping(false);
  }, []);

  const handleConversion = async () => {
    // Cancela animação anterior se houver
    if (cancelTypingRef.current) {
      cancelTypingRef.current();
      cancelTypingRef.current = null;
    }

    setResult("");
    setError("");
    setUsage(null);

    if (!purpose || !code.trim()) {
      alert("Por favor, insira um código válido e selecione o propósito.");
      return;
    }

    const isValid = validateCode(code, purpose);
    if (!isValid) {
      setError("Erro: Código não está estruturado corretamente. Corrija os problemas antes de tentar novamente.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("/api/generate-automation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, purpose }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError("Erro " + res.status + ": " + (data?.message ?? "Falha no servidor"));
        return;
      }

      const script = data?.script ?? "";

      if (!script) {
        setError("Resposta OK, mas o campo 'script' veio vazio.");
        return;
      }

      setIsTyping(true);

      const stop = startTypeEffect(script, setResult, 6);
      cancelTypingRef.current = () => {
        stop();
        setResult(script);
        setIsTyping(false);
      };

      // Garante que ao final o estado seja exatamente script
      const totalMs = script.length * 6 + 200;
      setTimeout(() => {
        setIsTyping(false);
        setResult(script);
        cancelTypingRef.current = null;
      }, totalMs);

      setUsage(data.usage ?? null);

      addToHistory({
        date: new Date().toISOString(),
        type: purpose,
        details: "Geração via backend (" + (data.usage?.provider ?? "unknown") + ")",
      });
    } catch (err) {
      setError("Erro na requisição: " + String(err?.message ?? err));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (cancelTypingRef.current) cancelTypingRef.current();
    navigator.clipboard.writeText(result ?? "").then(() => {
      setCopyMessage("Copiado!");
      setTimeout(() => setCopyMessage(""), 2000);
    });
  };

  const handleDownload = () => {
    if (cancelTypingRef.current) cancelTypingRef.current();
    const blob = new Blob([result], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = purpose === "Postman" ? "postman_tests.js" : "cypress_spec.cy.js";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Container
      sx={{
        backgroundColor: darkMode ? "#121212" : "#ffffff",
        color: darkMode ? "#ffffff" : "#000000",
        borderRadius: "8px",
        padding: "16px",
      }}
    >
      <Paper
        elevation={2}
        sx={{
          padding: "16px",
          marginBottom: "24px",
          marginTop: "16px",
          backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="body1" gutterBottom>
          Bem-vindo à página de conversão de scripts automatizados. Aqui você
          pode transformar:
        </Typography>
        <ul>
          <li>
            <strong>Cypress:</strong> Insira códigos HTML e gere testes
            automatizados.
          </li>
          <li>
            <strong>Postman:</strong> Insira objetos JSON para gerar scripts de
            validação.
          </li>
        </ul>
      </Paper>

      <FormControl
        fullWidth
        margin="normal"
        variant="outlined"
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderWidth: "2px",
              borderColor: darkMode ? "#ffffff" : "#1d4f91",
            },
            "&:hover fieldset": {
              borderColor: darkMode ? "#90caf9" : "#1d4f91",
            },
            "&.Mui-focused fieldset": {
              borderColor: darkMode ? "#90caf9" : "#1d4f91",
              borderWidth: "2px",
            },
          },
          "& .MuiInputLabel-root": {
            backgroundColor: darkMode ? "#121212" : "#ffffff",
            color: darkMode ? "#ffffff" : "#000000",
            padding: "0 4px",
          },
        }}
      >
        <InputLabel id="purpose-select-label">Propósito</InputLabel>
        <Select
          labelId="purpose-select-label"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          sx={{
            backgroundColor: darkMode ? "#1d1d1d" : "#ffffff",
            color: darkMode ? "#ffffff" : "#000000",
          }}
        >
          <MenuItem value="Cypress">Cypress</MenuItem>
          <MenuItem value="Postman">Postman</MenuItem>
        </Select>
      </FormControl>

      <Box mt={2}>
        <Typography variant="h6">Insira o Código:</Typography>
        <CodeMirror
          value={code}
          options={{
            mode: purpose === "Postman" ? "javascript" : "htmlmixed",
            theme: darkMode ? "dracula" : "material",
            lineNumbers: true,
          }}
          onBeforeChange={(editor, data, value) => setCode(value)}
        />
      </Box>

      <Button
        type="button"
        variant="contained"
        color="primary"
        onClick={handleConversion}
        disabled={!purpose || !code || isTyping || loading}
        sx={{
          mt: 2,
          backgroundColor: darkMode ? "#90caf9" : "#1d4f91",
          color: darkMode ? "#000000" : "#ffffff",
          "&:hover": {
            backgroundColor: darkMode ? "#64b5f6" : "#154b8b",
          },
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "Converter"}
      </Button>

      <Box mt={4} sx={{ position: "relative" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="h6">Script Gerado:</Typography>
          {isTyping && (
            <Button size="small" variant="outlined" onClick={handleShowFull} sx={{ fontSize: "11px", py: 0.2 }}>
              Mostrar completo
            </Button>
          )}
        </Box>
        <Paper
          elevation={3}
          sx={{
            padding: "16px",
            backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
            color: darkMode ? "#ffffff" : "#000000",
            border: darkMode ? "1px solid #333" : "1px solid #ccc",
            position: "relative",
          }}
        >
          {error ? (
            <Typography color="error" sx={{ fontFamily: "monospace" }}>
              {error}
            </Typography>
          ) : (
            <pre
              ref={preRef}
              style={{
                margin: 0,
                whiteSpace: "pre",
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: "60vh",
                minHeight: "200px",
                fontFamily: "'Courier New', Courier, monospace",
                fontSize: "13px",
                lineHeight: "1.6",
                color: darkMode ? "#ffffff" : "#222222",
              }}
            >
              {result}
            </pre>
          )}
          <Box sx={{ position: "absolute", top: "8px", right: "8px", display: "flex", gap: 0.5 }}>
            {result && (
              <IconButton
                onClick={handleDownload}
                title="Baixar script"
                sx={{
                  backgroundColor: darkMode ? "#4caf50" : "#2e7d32",
                  color: "#ffffff",
                  width: 32,
                  height: 32,
                }}
              >
                <Typography variant="caption" sx={{ color: "white", fontSize: "10px", fontWeight: "bold" }}>DL</Typography>
              </IconButton>
            )}
            <IconButton
              onClick={handleCopy}
              sx={{
                backgroundColor: darkMode ? "#ffffff" : "#1d4f91",
                color: darkMode ? "#1d1d1d" : "#ffffff",
                width: 32,
                height: 32,
              }}
            >
              {copyMessage ? (
                <Typography variant="caption" sx={{ color: darkMode ? "#000" : "white", fontSize: "10px" }}>
                  {copyMessage}
                </Typography>
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Paper>
      </Box>

      {usage && (
        <Box mt={2}>
          <Typography variant="subtitle1">Métricas de uso</Typography>
          <Paper elevation={1} sx={{ padding: 1, mt: 1, backgroundColor: darkMode ? "#111" : "#fafafa" }}>
            <div style={{ fontFamily: "monospace", fontSize: 13 }}>
              <div><strong>Provider:</strong> {usage.provider ?? "-"}</div>
              <div><strong>Model:</strong> {usage.model ?? "-"}</div>
              <div><strong>Prompt tokens:</strong> {usage.promptTokens ?? "-"}</div>
              <div><strong>Completion tokens:</strong> {usage.completionTokens ?? "-"}</div>
              <div><strong>Total tokens:</strong> {usage.totalTokens ?? "-"}</div>
              <div><strong>Prompt chars:</strong> {usage.promptChars ?? "-"}</div>
              <div><strong>Response chars:</strong> {usage.responseChars ?? "-"}</div>
              <div><strong>Cost estimate:</strong> {usage.costEstimate ?? "-"}</div>
            </div>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default Automation;
